import React, { useState } from 'react';
import { Box, Grid, Typography, Container, Stack, useTheme, styled ,Button} from '@mui/material';
import projects from '../images/projects.jpg';
import pos from '../data/pos.json';
import back1 from '../images/tm.jpg';
import mobile from '../data/mobile.json';
import DialogComponent from '../components/DialogComponent';
import webTik from '../images/tik.png';

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 4),
    borderRadius: '50px',
    backgroundColor: '#fff',
    color: '#000',
    fontWeight: 'bold',
    textTransform: 'none',
    boxShadow: theme.shadows[3],
    transition: 'background-color 0.3s, transform 0.3s, color 0.3s', // Added 'color 0.3s'
    '&:hover': {
        backgroundColor: '#a974d0', // Changed hover background color
        color: '#fff', // Added hover text color
        transform: 'translateY(-2px)',
    },
}));

// Custom Project Card Component
const ProjectCard = ({ data, onClick }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: theme.shadows[4],
        overflow: 'hidden',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '&:hover': {
          transform: 'translateY(-10px)',
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <Box
        sx={{
          height: 200,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={data.image1}
          alt={data.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />
      </Box>
      <Box sx={{ p: 3, flexGrow: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 'bold',
            mb: 1,
            color: theme.palette.text.primary,
          }}
        >
          {data.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            color: theme.palette.text.secondary,
          }}
        >
          {data.intro}
        </Typography>
      </Box>
      <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <button
          onClick={() => onClick(data)}
          style={{
            width: '100%',
            padding: '12px',
            border: `2px solid #a974d0`, // Updated border color
            backgroundColor: '#a974d0', // Updated background color
            color: 'white',
            borderRadius: '50px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
            fontFamily: "Montserrat, sans-serif",
            transition: 'background-color 0.3s ease, color 0.3s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#a974d0', // Updated hover text color
            },
          }}
        >
          More Info
        </button>
      </Box>
    </Box>
  );
};

// ... (rest of the Projects component remains unchanged)

function Projects() {
  const [rowData, setRowData] = useState({});
  const [handleOpen, setHandleOpen] = useState(false);
  const theme = useTheme();

  function moreInfo(data) {
    setRowData(data);
    setHandleOpen(true);
  }

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          height: { md: '60vh', xs: '40vh' },
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${projects})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          textAlign: 'center',
          p: 2,
        }}
        className='no-select'
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: '600',
            fontSize: { xs: '3rem', md: '6rem' },
            mb: 2,
          }}
        >
          Our Projects
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: '500',
            fontSize: { xs: '0.875rem', md: '1.2rem' },
          }}
        >
          <a href='/' style={{ textDecoration: 'none', color: 'white' }}>Home</a> / <span style={{ color: theme.palette.secondary.main }}>Projects</span>
        </Typography>
      </Box>

      {/* POS Systems Section */}
      <Container maxWidth="lg" sx={{ my: { xs: 8, md: 12 } }}>
        <Stack spacing={2} direction="row" alignItems="center" mb={6}>
          <Box
            component="img"
            src={webTik}
            alt='web icon'
            sx={{ height: '30px', width: 'auto' }}
          />
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 'bold',
            }}
          >
            Point Of Sale Systems
          </Typography>
        </Stack>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {pos.map((data, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <ProjectCard data={data} onClick={moreInfo} />
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Mobile Apps Section */}
      <Container maxWidth="lg" sx={{ my: { xs: 8, md: 12 } }}>
        <Stack spacing={2} direction="row" alignItems="center" mb={6}>
          <Box
            component="img"
            src={webTik}
            alt='mobile icon'
            sx={{ height: '30px', width: 'auto' }}
          />
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 'bold',
            }}
          >
            Mobile Applications
          </Typography>
        </Stack>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {mobile.map((data, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <ProjectCard data={data} onClick={moreInfo} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Dialog Component */}
      <DialogComponent
        rowData={rowData}
        setRowData={setRowData}
        handleOpen={handleOpen}
        setHandleOpen={setHandleOpen}
      />
      <Box
                sx={{
                    height: '35vh',
                    backgroundImage: `url(${back1})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: '#fff',
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: '500',
                            fontSize: { md: '6rem', xs: '4rem' },
                        }}
                    >
                        Let's Start
                    </Typography>
                    <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontFamily: 'Montserrat, sans-serif',
                                                        fontWeight: '700',
                                                        fontSize: { md: '1.25rem', xs: '1rem' },
                                                        mt: 2
                                                    }}
                                                    >
                                                    Empowering businesses through custom software solutions.{' '}
                                                    <span style={{ color: '#a974d0' }}>Your success, our code.</span>
                                                </Typography>
                    <StyledButton href="/contactus">Get In Touch</StyledButton>
                </Box>
            </Box>
    </>

  );
}

export default Projects;